import { useState, type FormEvent } from "react";
import { Banner, Button, Card, Checkbox, Dialog, DialogClose, EmptyState, Select, Spinner, TextField, useToast } from "@vavr/ui";
import { api, ApiError } from "../shared/api.ts";
import { navigate } from "../shared/router.tsx";
import { useAsync } from "../shared/useAsync.ts";
import { dateTime } from "../shared/format.ts";
import type { SharePayload } from "../shared/platform.ts";
import { G, useGuardian } from "./context.tsx";
import { normalisePhone, type Delegation } from "./People.tsx";
import type { GuardianSite } from "./status.ts";

const MAX_MS = 7 * 86_400_000;

/** End of the chosen option, in the phone's local time: today 11:59 pm, tomorrow 11:59 pm, or a date. */
export function untilMs(choice: string, customDate: string, now = Date.now()): number | null {
  const end = (d: Date) => { d.setHours(23, 59, 0, 0); return d.getTime(); };
  if (choice === "today") return end(new Date(now));
  if (choice === "tomorrow") { const d = new Date(now); d.setDate(d.getDate() + 1); return end(d); }
  if (choice === "date" && customDate) return end(new Date(`${customDate}T12:00:00`));
  return null;
}

/** A delegation may last at most 7 days (the server enforces the same rule). */
export const withinLimit = (until: number | null, now = Date.now()) => until != null && until > now && until - now <= MAX_MS;

interface Created { code: string; delegation_id: string; name: string; until: number; site: GuardianSite["site"]; children: string[] }
interface LocalDelegation { id: string; name: string; code: string; site_id: string }

export function CodeCard({ c, share }: { c: Created; share?: (p: SharePayload) => Promise<void> }) {
  const text = `${c.name.split(" ")[0]}, here is your code to collect ${c.children.join(" and ")} from ${c.site.name}: ${c.code}. At the door, tap "Someone with a code" and type it in. It works until ${dateTime(c.until, c.site.tz)}.`;
  const [copied, setCopied] = useState(false);
  return (
    <Card title={`Code for ${c.name}`} description={`Valid until ${dateTime(c.until, c.site.tz)}. We also sent it to them if you gave a phone or email.`}>
      <p className="pmu-g-code" aria-label={`Code ${c.code.split("").join(" ")}`}>{c.code}</p>
      <div className="pmu-stack">
        {share && <Button icon="upload" onClick={() => share({ title: "Pickup code", text }).catch(() => undefined)} fullWidth>Share the code</Button>}
        <Button variant="secondary" onClick={() => navigator.clipboard?.writeText(text).then(() => setCopied(true), () => undefined)} fullWidth>{copied ? "Copied" : "Copy message"}</Button>
      </div>
    </Card>
  );
}

function ActiveDelegations({ s, local, onRevoked }: { s: GuardianSite; local: Map<string, LocalDelegation>; onRevoked: () => void }) {
  const toast = useToast();
  const { version } = useGuardian();
  const q = useAsync(async () => {
    const all = new Map<string, Delegation>();
    for (const c of s.children) {
      const r = await api<{ delegations: Delegation[] }>("GET", `/api/v1/guardian/sites/${s.site.id}/children/${c.id}/adults`);
      for (const d of r.delegations) all.set(d.id, d);
    }
    return [...all.values()].filter((d) => d.status === "active" && d.valid_to > Date.now());
  }, [s.site.id, version]);
  if (q.loading && !q.data) return <Spinner label="Loading codes" />;
  if (!q.data?.length) return null;
  const names = new Map(s.children.map((c) => [c.id, c.first]));
  return (
    <Card title="Active codes" description={s.site.name}>
      <ul className="pmu-list">
        {q.data.map((d) => (
          <li key={d.id} className="pmu-row pmu-row--between">
            <span>
              <strong>{local.get(d.id)?.name ?? "Someone you asked"}</strong>
              <span className="pmu-small pmu-muted"> · {d.child_ids.map((id) => names.get(id)).filter(Boolean).join(", ")} · until {dateTime(d.valid_to, s.site.tz)}{d.used_seq ? " · used" : ""}</span>
            </span>
            <Dialog title="Cancel this code?" description="It stops working at the door right away."
              trigger={<Button size="sm" variant="ghost">Cancel code</Button>}
              actions={<><DialogClose><Button variant="secondary">Keep it</Button></DialogClose><DialogClose><Button variant="danger" onClick={async () => {
                try { await api("DELETE", `/api/v1/guardian/sites/${s.site.id}/delegations/${d.id}`); toast.show({ title: "Code canceled", tone: "success" }); q.reload(); onRevoked(); }
                catch (e) { toast.show({ title: (e as ApiError).message, tone: "danger" }); }
              }}>Cancel code</Button></DialogClose></>} />
          </li>
        ))}
      </ul>
    </Card>
  );
}

export function Delegate() {
  const { overview, platform, kv } = useGuardian();
  const sites = overview.sites.filter((s) => s.children.length);
  const [siteId, setSiteId] = useState(sites[0]?.site.id ?? "");
  const s = sites.find((x) => x.site.id === siteId);
  const [f, setF] = useState({ first: "", last: "", phone: "", email: "", until: "today", date: "" });
  const [kids, setKids] = useState<string[]>(() => (s?.children.length === 1 ? [s.children[0]!.id] : []));
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState<Created | null>(null);
  const localQ = useAsync(() => kv.get<LocalDelegation[]>("delegations").then((l) => new Map((l ?? []).map((d) => [d.id, d]))), [created]);
  if (!s) return <EmptyState icon="child" title="No children linked yet" />;
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF((x) => ({ ...x, [k]: e.target.value }));
  const until = untilMs(f.until, f.date);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!kids.length) { setError("Choose at least one child."); return; }
    if (!withinLimit(until)) { setError("A one-time code can last up to 7 days."); return; }
    setBusy(true);
    try {
      const r = await api<{ code: string; delegation_id: string }>("POST", `/api/v1/guardian/sites/${s!.site.id}/delegations`, {
        first: f.first, last: f.last, phone: f.phone ? normalisePhone(f.phone) : null, email: f.email || undefined, child_ids: kids, valid_to: until,
      });
      const name = `${f.first} ${f.last}`.trim();
      const prev = (await kv.get<LocalDelegation[]>("delegations")) ?? [];
      await kv.set("delegations", [...prev.slice(-49), { id: r.delegation_id, name, code: r.code, site_id: s!.site.id }]);
      setCreated({ code: r.code, delegation_id: r.delegation_id, name, until: until!, site: s!.site, children: s!.children.filter((c) => kids.includes(c.id)).map((c) => c.first) });
    } catch (err) {
      setError((err as ApiError).message);
    } finally { setBusy(false); }
  }

  if (created) {
    return (
      <div className="pmu-stack">
        <CodeCard c={created} share={platform.share} />
        <Button variant="ghost" onClick={() => { setCreated(null); setF({ first: "", last: "", phone: "", email: "", until: "today", date: "" }); }} fullWidth>Done</Button>
      </div>
    );
  }

  const today = new Date();
  const maxDate = new Date(Date.now() + 6 * 86_400_000);
  const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  return (
    <div className="pmu-stack">
      <form className="pmu-stack" onSubmit={submit}>
        <h2 className="pmu-g-h2">My neighbor today</h2>
        <p className="pmu-muted">They get a one-time code to show at the door. No app, no account.</p>
        {error && <Banner tone="danger" title={error} />}
        {sites.length > 1 && <Select label="School or center" value={siteId} onValueChange={(v) => { setSiteId(v ?? ""); setKids([]); }} options={sites.map((x) => ({ value: x.site.id, label: x.site.name }))} />}
        <div className="pmu-grid-2">
          <TextField label="First name" required value={f.first} onChange={set("first")} />
          <TextField label="Last name" required value={f.last} onChange={set("last")} />
        </div>
        <TextField label="Their mobile (optional)" type="tel" inputMode="tel" value={f.phone} onChange={set("phone")} hint="We text them the code." />
        <TextField label="Their email (optional)" type="email" value={f.email} onChange={set("email")} />
        <fieldset className="pmu-g-fieldset">
          <legend>Children</legend>
          {s.children.map((c) => (
            <Checkbox key={c.id} label={c.first} checked={kids.includes(c.id)} onCheckedChange={(on) => setKids((k) => (on ? [...k, c.id] : k.filter((x) => x !== c.id)))} />
          ))}
        </fieldset>
        <Select label="Until" value={f.until} onValueChange={(v) => setF((x) => ({ ...x, until: v ?? "today" }))} options={[
          { value: "today", label: "End of today" }, { value: "tomorrow", label: "End of tomorrow" }, { value: "date", label: "Choose a date (up to 7 days)" },
        ]} />
        {f.until === "date" && <TextField label="Last day" type="date" required min={iso(today)} max={iso(maxDate)} value={f.date} onChange={set("date")} />}
        <Button type="submit" loading={busy} fullWidth>Create the code</Button>
        <Button variant="ghost" onClick={() => navigate(`${G}/people`)} fullWidth>Cancel</Button>
      </form>
      {localQ.data && <ActiveDelegations s={s} local={localQ.data} onRevoked={() => undefined} />}
    </div>
  );
}
